/**
 * Store pour la gestion des offres de coaching
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';

export const useCoachingOfferStore = defineStore('offerCoaching', (() => {

    // State de la liste des offres (total, offers)
    const offerList = ref({});

    // State pour différencier les données de la liste des offres
    const offers = ref([]);

    /**
     * Retourne la liste des offres
     * @return array
     */
    const getOfferList = computed(() => offerList.value || {});

    /**
     * Retourne l'offre de coaching dont l'identifiant est en paramètre
     * @param int offerId
     * @return object|null
     */
    const getOfferById = computed(() => {
        return ((offerId) => {
            const index = offers.value.findIndex((offerFromState) => offerFromState.id === offerId);
            if(index === -1) {
                return null;
            }
            return offers.value[index];
        });
    });

    /**
     * Chargement de la liste des offres de coaching
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadOfferList = ((params) => {

        const path = '/api/admin/coaching-offers';

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetOfferList();
                }

                updateOfferList(response.data.data);
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Chargement des données d'une offre de coaching
     * @param int offerId
     * @return Promise
     */
    const loadOfferById = ((offerId) => {

        const existingOffer = getOfferById.value(offerId);
        if(existingOffer !== null) {
            return Promise.resolve();
        }

        const path = '/api/admin/coaching-offers/:offerId'.replace(':offerId', offerId);

        return axios.get(path).then((response) => {
            if(response && response.data && response.data.success && response.data.offer) {
                updateOffer(response.data.offer);
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Suppression d'une offre de coaching
     * @param object offer
     * @return Promise
     */
    const removeOffer = ((offer) => {

        const path = '/api/admin/coaching-offers/:offerId'.replace(':offerId', offer.id);

        return axios.delete(path).then((response) => {
            if(response && response.data && response.data.success) {
                onRemoveOffer(offer);
                return Promise.resolve();
            }
            return Promise.reject();
        });

    });

    /**
     * Supprime l'offre du store
     * @param object offer L'offre à supprimer
     * @return void
     */
    const onRemoveOffer = ((offer) => {

        // Suppression des offres déjà chargées
        const indexFromLoading = offers.value.findIndex((current) => current.id === offer.id);
        if(indexFromLoading !== -1) {
            offers.value.splice(indexFromLoading, 1);
        }

        // Vérifie que la liste a été chargée
        const currentDataList = getOfferList.value;
        if(currentDataList.total === undefined) {
            return;
        }

        // Suppression de la liste
        const newOfferList = currentDataList.offers;
        const indexFromList = newOfferList.findIndex((current) => current.id === offer.id);
        if(indexFromList !== -1) {
            newOfferList.splice(indexFromList, 1);
            offerList.value = {
                total: offerList.value.total - 1,
                offers: newOfferList
            };
        }

    });

    /**
     * Modifie une offre
     * @param object offer Données de l'offre de coaching à mettre à jour dans la liste
     * @return Promise
     */
    const updateOffer = ((offer) => {

        const currentOffers = offers.value;

        const index = currentOffers.findIndex((offerFromState) => offerFromState.id === offer.id);
        if(index !== -1) {
            offers.value[index] = { ...offers.value[index], ...offer };
        } else {
            offers.value.push(offer);
        }

    });

    /**
     * Modifie la liste des offres
     * @param data Données des offres : total et items
     * @return void
     */
    const updateOfferList = ((data) => {

        const currentData = offerList.value;
        if(currentData.total === undefined) {
            offerList.value = {
                total: data.total,
                offers: data.items
            };

            data.items.forEach((offer) => {
                updateOffer(offer);
            });

        } else {
            offerList.value.total = data.total;

            const currentOffers = offerList.value.offers;
            const newOffers = data.items.filter((item) => ! currentOffers.find((current) => current.id === item.id));
            offerList.value.offers = [ ...currentOffers, ...newOffers ];

            newOffers.forEach((offer) => {
                updateOffer(offer);
            });
        }

    });

    /**
     * Réinitialisation de la liste offres de coaching
     * @return void
     */
    const resetOfferList = (() => {
        offerList.value = {};
    });


    return {
        getOfferList,
        getOfferById,
        loadOfferList,
        loadOfferById,
        updateOffer,
        removeOffer
    };
}));
